import apiService from './api';
import {
  User,
  LoginRequest,
  LoginResponse,
  InitializePasswordRequest,
  ChangePasswordRequest,
  OTPSetupInfo,
  ApiResponse,
} from '../types';

class AuthService {
  // 用户登录
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await apiService.post<LoginResponse>('/api/v1/auth/login', credentials);
    
    // 保存用户信息和token
    if (response.token) {
      apiService.setToken(response.token);
      this.setUserInfo(response.user);
    }
    
    return response;
  }

  // 用户登出
  async logout(): Promise<void> {
    try {
      await apiService.post('/api/v1/auth/logout');
    } catch (error) {
      // 即使登出失败也要清除本地信息
      console.error('Logout failed:', error);
    } finally {
      this.clearAuth();
    }
  }

  // 获取当前用户信息
  async getCurrentUser(): Promise<User> {
    const response = await apiService.get<{ user: User }>('/api/v1/auth/profile');
    this.setUserInfo(response.user);
    return response.user;
  }

  // 初始化密码
  async initializePassword(data: InitializePasswordRequest): Promise<ApiResponse> {
    return apiService.post('/api/v1/auth/initialize-password', data);
  }

  // 修改密码
  async changePassword(data: ChangePasswordRequest): Promise<ApiResponse> {
    return apiService.post('/api/v1/auth/change-password', data);
  }

  // 获取OTP状态
  async getOTPStatus(): Promise<{ enabled: boolean; verified: boolean; has_secret: boolean; available_backup_codes: number; remaining_time: number }> {
    const response = await apiService.get<{ data: any }>('/api/v1/auth/otp/status');
    return response.data;
  }

  // 设置OTP
  async setupOTP(): Promise<OTPSetupInfo> {
    const response = await apiService.post<{ data: OTPSetupInfo }>('/api/v1/auth/otp/setup');
    return response.data;
  }

  // 启用OTP
  async enableOTP(otpCode: string): Promise<{ backup_codes: any[] }> {
    return apiService.post('/api/v1/auth/otp/enable', {
      code: otpCode
    });
  }

  // 验证OTP
  async verifyOTP(otpCode: string, useBackupCode = false): Promise<ApiResponse> {
    return apiService.post('/api/v1/auth/otp/verify', { 
      code: otpCode,
      use_backup_code: useBackupCode
    });
  }

  // 重新生成备用代码
  async regenerateBackupCodes(password: string, otpCode: string): Promise<{ backup_codes: any[] }> {
    return apiService.post('/api/v1/auth/otp/regenerate-backup-codes', {
      password,
      code: otpCode
    });
  }

  // 禁用OTP
  async disableOTP(password: string, otpCode: string): Promise<ApiResponse> {
    return apiService.post('/api/v1/auth/otp/disable', {
      password,
      otp_code: otpCode
    });
  }

  // 更新用户配置
  async updateProfile(data: { full_name?: string; email?: string; preferences?: any }): Promise<ApiResponse> {
    return apiService.put('/api/v1/profile', data);
  }

  // 发送邮箱验证邮件
  async sendEmailVerification(email: string): Promise<ApiResponse> {
    return apiService.post('/api/v1/send-email-verification', { email });
  }

  // 验证邮箱
  async verifyEmail(token: string): Promise<ApiResponse> {
    return apiService.post('/api/v1/verify-email', { token });
  }

  // 重新发送邮箱验证邮件
  async resendEmailVerification(): Promise<ApiResponse> {
    return apiService.post('/api/v1/resend-email-verification');
  }

  // 刷新Token
  async refreshToken(): Promise<{ token: string; expires_at: string }> {
    return apiService.post('/api/v1/auth/refresh');
  }

  // 检查认证状态
  isAuthenticated(): boolean {
    const token = apiService.getToken();
    const user = this.getUserInfo();
    return !!(token && user);
  }

  // 获取用户信息
  getUserInfo(): User | null {
    const userInfo = localStorage.getItem('user_info');
    if (userInfo) {
      try {
        return JSON.parse(userInfo);
      } catch (error) {
        console.error('Failed to parse user info:', error);
        localStorage.removeItem('user_info');
      }
    }
    return null;
  }

  // 设置用户信息
  setUserInfo(user: User): void {
    localStorage.setItem('user_info', JSON.stringify(user));
  }

  // 获取Token
  getToken(): string | null {
    return apiService.getToken();
  }

  // 设置Token
  setToken(token: string): void {
    apiService.setToken(token);
  }

  // 清除认证信息
  clearAuth(): void {
    apiService.clearToken();
    localStorage.removeItem('user_info');
  }

  // 检查是否需要初始化密码
  needsPasswordInitialization(): boolean {
    const user = this.getUserInfo();
    return user ? !user.is_initialized : false;
  }

  // 检查是否需要验证OTP
  needsOTPVerification(): boolean {
    const user = this.getUserInfo();
    return user ? (user.otp_enabled && !user.otp_verified) : false;
  }

  // 获取用户状态
  getUserStatus(): {
    isAuthenticated: boolean;
    needsPasswordInit: boolean;
    needsOTPVerification: boolean;
    user: User | null;
  } {
    const isAuthenticated = this.isAuthenticated();
    const user = this.getUserInfo();
    
    return {
      isAuthenticated,
      needsPasswordInit: this.needsPasswordInitialization(),
      needsOTPVerification: this.needsOTPVerification(),
      user,
    };
  }
}

export const authService = new AuthService();
export default authService;